// © 2025 Camp Promethean, LLC. All Rights Reserved.
// Delete Selection Dialog Component

import { useState } from 'react';
import { EnhancedSelection } from '@/types/selection';
import { useToast } from '@/components/ui/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { deleteSelection } from '@/lib/api/selections';
import { Loader2 } from 'lucide-react';

interface DeleteSelectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selection: EnhancedSelection;
  onSuccess?: () => void;
}

export function DeleteSelectionDialog({
  isOpen,
  onClose,
  selection,
  onSuccess
}: DeleteSelectionDialogProps) {
  const { toast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Handle selection deletion
  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      
      await deleteSelection(selection.id);
      
      toast({
        title: 'Selection Deleted',
        description: `Successfully deleted selection for ${selection.plantId}`,
        variant: 'default',
      });
      
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error deleting selection:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete selection. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };
  
  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Selection</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete the selection for <strong>{selection.plantId}</strong>?
            This action cannot be undone.
          </AlertDialogDescription>
          
          <div className="my-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-amber-800 text-sm">
            <p>
              <strong>Warning:</strong> Deleting this selection will remove all associated data, including:
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Selection metrics and ratings</li>
              <li>Tags and badges</li>
              <li>Selection rationale and highlights</li>
              <li>Any associated notes</li>
            </ul>
          </div>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction 
            onClick={(e) => {
              e.preventDefault();
              handleDelete();
            }}
            disabled={isDeleting}
            className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
          >
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              'Delete Selection'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
} 